import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { getOverlayRoot } from './overlay'
import { cx } from './cx'

export interface MenuItem {
  key: string
  label: ReactNode
  onSelect: () => void
  destructive?: boolean
  disabled?: boolean
}

export interface MenuProps {
  // Renders the trigger; spread the props onto a <button>
  trigger: (props: {
    ref: (el: HTMLButtonElement | null) => void
    onClick: () => void
    onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void
    'aria-haspopup': 'menu'
    'aria-expanded': boolean
    'aria-controls': string
  }) => ReactNode
  items: MenuItem[]
  align?: 'start' | 'end'
}

/**
 * Action menu (WAI-ARIA menu button). Renders in the overlay portal, positioned under the
 * trigger, so it isn't clipped by scrolling tables. Arrow keys move, Enter selects,
 * Escape or Tab closes and returns focus to the trigger.
 */
export function Menu({ trigger, items, align = 'end' }: MenuProps) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
  const menuId = useId()
  const enabled = items.map((item, i) => (item.disabled ? -1 : i)).filter(i => i >= 0)

  function close(returnFocus = true) {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const width = menuRef.current?.offsetWidth || 200
    setPosition({
      top: rect.bottom + 4,
      left: align === 'end' ? Math.max(8, rect.right - width) : rect.left,
    })
  }, [open, align])

  useEffect(() => {
    if (!open) return
    itemRefs.current[enabled[0]]?.focus()
    function onPointerDown(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node) && !triggerRef.current?.contains(e.target as Node)) close(false)
    }
    function onScroll() { close(false) }
    document.addEventListener('mousedown', onPointerDown)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  function onMenuKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const current = itemRefs.current.findIndex(el => el === document.activeElement)
    const pos = enabled.indexOf(current)
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      itemRefs.current[enabled[(pos + 1) % enabled.length]]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      itemRefs.current[enabled[(pos - 1 + enabled.length) % enabled.length]]?.focus()
    } else if (e.key === 'Home') {
      e.preventDefault()
      itemRefs.current[enabled[0]]?.focus()
    } else if (e.key === 'End') {
      e.preventDefault()
      itemRefs.current[enabled[enabled.length - 1]]?.focus()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      close()
    } else if (e.key === 'Tab') {
      close(false)
    }
  }

  return (
    <>
      {trigger({
        ref: el => { triggerRef.current = el },
        onClick: () => setOpen(o => !o),
        onKeyDown: e => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setOpen(true)
          }
        },
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': menuId,
      })}
      {open && createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          onKeyDown={onMenuKeyDown}
          style={{ position: 'fixed', top: position?.top ?? -9999, left: position?.left ?? -9999 }}
          className="min-w-[200px] rounded-lg border border-line bg-surface py-1 text-ink shadow-elevated"
        >
          {items.map((item, i) => (
            <button
              key={item.key}
              ref={el => { itemRefs.current[i] = el }}
              type="button"
              role="menuitem"
              tabIndex={-1}
              disabled={item.disabled}
              onClick={() => {
                close()
                item.onSelect()
              }}
              className={cx(
                'flex w-full items-center px-3 py-2 text-left text-sm focus:outline-none focus:bg-surface-raised hover:bg-surface-raised',
                'disabled:cursor-not-allowed disabled:opacity-50',
                item.destructive ? 'text-critical' : 'text-ink'
              )}
            >
              {item.label}
            </button>
          ))}
        </div>,
        getOverlayRoot()
      )}
    </>
  )
}
